import { useState, useRef, useEffect } from "react";
import { motion } from "motion/react";
import { Lock, Users, Shield, Zap, Eye, Camera, AlertTriangle, Search } from "lucide-react";
import { MysticalBg, MP } from "./MysticalBg";
import { SiteHeader } from "./SiteHeader";
import { SiteFooter } from "./SiteFooter";
import { SecurityModal } from "./SecurityModal";
import { DT } from "./DeltaTypography";

const logoImg = "/assets/new-logo.png";
const P = {
  parchment: MP.parchment, parchmentDim: MP.parchmentDim,
};

/* ── Heavy text-shadow for readability over busy backgrounds ── */
const TXT_POWER = "0 0 6px #000, 0 0 12px #000, 0 0 24px #000, 0 2px 4px rgba(0,0,0,0.95)";
const TXT_GOLD_POWER = "0 0 10px rgba(255,160,40,0.8), 0 0 28px rgba(200,120,20,0.5), 0 0 6px #000, 0 0 14px #000, 0 2px 4px rgba(0,0,0,0.95)";
const TXT_RED_POWER = "0 0 10px rgba(168,16,48,0.8), 0 0 28px rgba(107,0,21,0.5), 0 0 6px #000, 0 0 14px #000, 0 2px 4px rgba(0,0,0,0.95)";

const CARDS = [
  { icon: Camera, glyph: "\u0394", greek: "\u03A3\u039A\u039F\u03A0\u039F\u03A3", label: "VIGILANCIA TOTAL", phrase: "Tu c\u00e1mara te mira, tu micro te escucha y tu historial te condena.", sub: "Nada es privado.", col: "rgba(255,160,40,0.9)", border: "rgba(200,120,20,0.45)", glow: "rgba(200,120,20,0.25)" },
  { icon: Shield, glyph: "\u03A8", greek: "\u0391\u039B\u0397\u0398\u0395\u0399\u0391", label: "ILUSI\u00d3N DE SEGURIDAD", phrase: "La seguridad es una ilusi\u00f3n; solo existe el retraso de lo inevitable.", sub: "Prot\u00e9gete o asume el costo.", col: "rgba(255,160,40,0.9)", border: "rgba(200,120,20,0.45)", glow: "rgba(200,120,20,0.25)" },
  { icon: Search, glyph: "\u03A9", greek: "\u039A\u03A1\u03A5\u03A0\u03A4\u039F\u03A3", label: "LO QUE YA TE ROBARON", phrase: "Si crees que no tienes nada que ocultar, es porque no sabes todo lo que ya te robaron.", sub: "", col: "rgba(168,16,48,0.9)", border: "rgba(107,0,21,0.5)", glow: "rgba(107,0,21,0.22)" },
  { icon: AlertTriangle, glyph: "\u03A3", greek: "\u0394\u0399\u039A\u0391\u0399\u039F\u03A3", label: "SIN ACCIDENTES", phrase: "En la red no hay accidentes, solo v\u00edctimas que ignoraron las se\u00f1ales.", sub: "", col: "rgba(168,16,48,0.9)", border: "rgba(107,0,21,0.5)", glow: "rgba(107,0,21,0.22)" },
];

const POSITIONS = [
  { top: 10, left: 10 },
  { top: 10, right: 10 },
  { bottom: 10, left: 10 },
  { bottom: 10, right: 10 },
];

function CornerCard({ card, idx }) {
  const Icon = card.icon;
  const isGold = idx < 2;
  return (
    <motion.div
      data-testid={`service-card-${idx}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, delay: 0.3 + idx * 0.15 }}
      className="absolute flex-col gap-1.5 p-3 rounded-sm hidden xl:flex"
      style={{
        ...POSITIONS[idx],
        width: "clamp(200px,16vw,240px)",
        zIndex: 10,
        background: "rgba(4,2,8,0.93)",
        backdropFilter: "blur(16px)",
        border: `1px solid ${card.border}`,
        boxShadow: `0 0 30px ${card.glow}, inset 0 0 20px rgba(0,0,0,0.5)`,
      }}
    >
      <div className="flex items-center gap-2">
        <Icon size={14} color={card.col} style={{ filter: `drop-shadow(0 0 6px ${card.col})` }} />
        <span style={{ fontFamily: DT.greek, fontSize: "1.1rem", color: card.col, lineHeight: 1, textShadow: isGold ? TXT_GOLD_POWER : TXT_RED_POWER }}>{card.glyph}</span>
        <span style={{ fontFamily: DT.data, fontSize: "0.55rem", color: card.col, letterSpacing: "0.2em", opacity: 0.8, textShadow: TXT_POWER, fontWeight: 500 }}>{card.greek}</span>
      </div>
      <p style={{ fontFamily: DT.decree, fontSize: "0.75rem", color: card.col, letterSpacing: "0.1em", textShadow: isGold ? TXT_GOLD_POWER : TXT_RED_POWER, fontWeight: 600 }}>{card.label}</p>
      <div style={{ height: 1, background: `linear-gradient(90deg, ${card.col}55, transparent)` }} />
      <p style={{ fontFamily: DT.notebook, fontSize: "0.68rem", color: "rgba(255,255,255,0.92)", lineHeight: 1.7, letterSpacing: "0.04em", textShadow: TXT_POWER }}>
        "{card.phrase}{card.sub && <em style={{ color: card.col }}> {card.sub}</em>}"
      </p>
      <div className="absolute top-0 left-0 w-4 h-4" style={{ borderTop: `1px solid ${card.col}66`, borderLeft: `1px solid ${card.col}66` }} />
      <div className="absolute top-0 right-0 w-4 h-4" style={{ borderTop: `1px solid ${card.col}44`, borderRight: `1px solid ${card.col}44` }} />
      <div className="absolute bottom-0 left-0 w-4 h-4" style={{ borderBottom: `1px solid ${card.col}44`, borderLeft: `1px solid ${card.col}44` }} />
      <div className="absolute bottom-0 right-0 w-4 h-4" style={{ borderBottom: `1px solid ${card.col}66`, borderRight: `1px solid ${card.col}66` }} />
    </motion.div>
  );
}

function AnimCounter({ target, suffix = "" }) {
  const [val, setVal] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const dur = 2400, start = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - start) / dur, 1);
      setVal(Math.round((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) requestAnimationFrame(tick);
    };
    setTimeout(() => requestAnimationFrame(tick), 700);
  }, [target]);
  return <span>{val.toLocaleString()}{suffix}</span>;
}

const STATS = [
  { icon: Users, val: 1847, suffix: "+", label: "CLIENTES PROTEGIDOS", col: "rgba(155,0,255,0.95)" },
  { icon: Shield, val: 432, suffix: "+", label: "CASOS RESUELTOS", col: "rgba(168,16,48,1)" },
  { icon: Eye, val: 98, suffix: "%", label: "TASA DE \u00c9XITO", col: "rgba(255,160,40,0.95)" },
  { icon: Zap, val: 24, suffix: "/7", label: "MONITOREO CONTINUO", col: "rgba(168,16,48,1)" },
];

export function HomeDelta() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div data-testid="home-delta" style={{ height: "100vh", overflow: "hidden", display: "flex", flexDirection: "column", fontFamily: DT.greek, color: P.parchment, background: MP.abyss, position: "relative" }}>
      <MysticalBg variant="home" />
      <SiteHeader />

      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>

        {CARDS.map((card, idx) => (
          <CornerCard key={idx} card={card} idx={idx} />
        ))}

        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 5 }}>

          <motion.p
            animate={{ textShadow: [TXT_GOLD_POWER, "0 0 18px rgba(255,160,40,1), 0 0 40px rgba(200,120,20,0.7), 0 0 6px #000, 0 0 14px #000, 0 2px 4px rgba(0,0,0,0.95)", TXT_GOLD_POWER] }}
            transition={{ duration: 4, repeat: Infinity }}
            style={{ fontFamily: DT.data, fontSize: "clamp(0.7rem,1.2vw,0.9rem)", color: "rgba(255,255,255,0.95)", letterSpacing: "0.5em", marginBottom: 6, textShadow: TXT_GOLD_POWER, fontWeight: 500 }}>
            — OMNISCIENT OBSERVER —
          </motion.p>

          <div className="relative flex items-center justify-center" style={{ width: "clamp(280px,35vw,450px)", height: "clamp(280px,35vw,450px)" }}>
            {[
              { inset: "0%", dur: 26, dir: 1, col: "rgba(107,0,21,0.22)" },
              { inset: "10%", dur: 18, dir: -1, col: "rgba(200,120,20,0.2)" },
              { inset: "20%", dur: 12, dir: 1, col: "rgba(107,0,21,0.3)" },
            ].map((ring, ri) => (
              <motion.div key={ri} className="absolute rounded-full pointer-events-none"
                style={{ inset: ring.inset, border: `1px solid ${ring.col}` }}
                animate={{ rotate: ring.dir > 0 ? [0, 360] : [360, 0], opacity: [0.3, 0.7, 0.3] }}
                transition={{ rotate: { duration: ring.dur, repeat: Infinity, ease: "linear" }, opacity: { duration: 4, repeat: Infinity } }}
              />
            ))}
            <motion.div className="absolute rounded-full pointer-events-none"
              style={{ inset: "25%", background: "radial-gradient(circle, rgba(90,0,12,0.55) 0%, transparent 75%)", filter: "blur(24px)" }}
              animate={{ opacity: [0.5, 1, 0.5], scale: [0.9, 1.1, 0.9] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.button data-testid="logo-button" onClick={() => setModalOpen(true)} whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.95 }}
              animate={{ y: [0, -8, 0] }} transition={{ y: { duration: 5, repeat: Infinity, ease: "easeInOut" } }}
              style={{ background: "transparent", border: "none", cursor: "pointer", padding: 14, position: "relative", zIndex: 5 }}>
              <motion.img src={logoImg} alt="Delta Protec"
                style={{ width: "clamp(240px,30vw,400px)", height: "clamp(240px,30vw,400px)", objectFit: "contain", mixBlendMode: "lighten" }}
                animate={{ filter: ["drop-shadow(0 0 14px rgba(107,0,21,0.65)) drop-shadow(0 0 40px rgba(80,0,10,0.35))", "drop-shadow(0 0 30px rgba(107,0,21,0.95)) drop-shadow(0 0 70px rgba(60,0,8,0.5))", "drop-shadow(0 0 14px rgba(107,0,21,0.65)) drop-shadow(0 0 40px rgba(80,0,10,0.35))"] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.button>
          </div>

          <motion.span
            animate={{ opacity: [0.7, 1, 0.7], textShadow: [TXT_GOLD_POWER, "0 0 14px rgba(255,160,40,1), 0 0 30px rgba(200,120,20,0.8), 0 0 6px #000, 0 0 14px #000", TXT_GOLD_POWER] }}
            transition={{ duration: 2.5, repeat: Infinity }}
            style={{ fontFamily: DT.terminal, fontSize: "0.85rem", color: "rgba(255,160,40,1)", letterSpacing: "0.25em", marginTop: 4, textShadow: TXT_GOLD_POWER, fontWeight: 600 }}>
            TOCA PARA INGRESAR
          </motion.span>

          <motion.button data-testid="access-intel-btn" onClick={() => setModalOpen(true)} whileHover={{ scale: 1.04, boxShadow: "0 0 50px rgba(107,0,21,0.75)" }} whileTap={{ scale: 0.97 }}
            className="flex items-center gap-2 px-7 py-2.5 rounded-sm"
            style={{ marginTop: 10, border: "1px solid rgba(107,0,21,0.75)", background: "linear-gradient(135deg, rgba(107,0,21,0.4), rgba(6,4,12,0.95))", backdropFilter: "blur(12px)", cursor: "pointer", fontFamily: DT.decree, fontSize: "0.65rem", color: "rgba(168,16,48,1)", letterSpacing: "0.22em", boxShadow: "0 0 22px rgba(107,0,21,0.45), 0 0 50px rgba(80,0,10,0.2)", textShadow: TXT_RED_POWER, fontWeight: 600 }}>
            <Lock size={14} color="rgba(168,16,48,0.95)" style={{ filter: "drop-shadow(0 0 5px rgba(168,16,48,0.8))" }} />
            ACCEDER · UNIDAD DE INTELIGENCIA
            <Lock size={14} color="rgba(168,16,48,0.95)" style={{ filter: "drop-shadow(0 0 5px rgba(168,16,48,0.8))" }} />
          </motion.button>

          <p style={{ fontFamily: DT.data, fontSize: "0.55rem", color: "rgba(255,255,255,0.85)", letterSpacing: "0.22em", marginTop: 5, textShadow: TXT_POWER, fontWeight: 400 }}>
            ACCESO RESTRINGIDO · LEE EL PROTOCOLO ANTES DE INGRESAR
          </p>

        </div>

      </div>

      <SiteFooter />
      <SecurityModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
